import { LockClosedIcon, PencilIcon } from '@heroicons/react/24/outline';
import { clsx } from 'clsx';
import { useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { NavLink, useSearchParams } from 'react-router';
import { DataTable } from 'softcat:components/data-table.tsx';
import { SearchParametersOrderBy } from 'softcat:components/search-parameters-order-by.tsx';
import { SearchParametersPagination } from 'softcat:components/search-parameters-pagination.tsx';
import { SearchParametersSearchField } from 'softcat:components/search-parameters-search-field.tsx';
import { Spinner } from 'softcat:components/spinner.tsx';
import { useTerritory } from 'softcat:hooks/use-territory.ts';
import { UserService } from 'softcat:services/user.ts';
import useSWR, { preload } from 'swr';

export const Users = () => {
    const intl = useIntl();
    const { activeTerritory } = useTerritory();
    const [searchParameters] = useSearchParams();
    const limit = Number.parseInt(searchParameters.get('limit') || '10');
    const page = Number.parseInt(searchParameters.get('page') || '1');
    const query = searchParameters.get('query') || '';
    const orderBy = searchParameters.get('orderBy') || '';

    const { data, error, isLoading } = useSWR({ key: `${activeTerritory}-UserServiceFindAll`, limit, orderBy, page: page - 1, query }, UserService.findAll);

    const columns = useMemo(() => {
        return [
            { key: 'username', value: intl.formatMessage({ id: 'USERS_PAGE_COLUMN_USERNAME' }), visible: true },
            { key: 'email', value: intl.formatMessage({ id: 'USERS_PAGE_COLUMN_EMAIL' }), visible: true },
            { key: 'type', value: intl.formatMessage({ id: 'USERS_PAGE_COLUMN_ACCOUNT_TYPE' }), visible: true },
            { key: 'roles', value: intl.formatMessage({ id: 'USERS_PAGE_COLUMN_ROLES' }), visible: true },
            { key: 'actions', value: intl.formatMessage({ id: 'USERS_PAGE_COLUMN_ACTIONS' }), visible: false },
        ];
    }, [intl]);

    const rows = useMemo(() => {
        return (
            data?.map((user, index) => {
                return {
                    actions: (
                        <div className={'flex gap-4 justify-self-end'}>
                            <NavLink
                                data-testid={`edit-user-${index}`}
                                data-tooltip-content={intl.formatMessage({ id: 'TOOLTIP_EDIT' })}
                                data-tooltip-id={'app-tooltip'}
                                onMouseEnter={() => {
                                    return preload({ id: user.id, key: `${activeTerritory}-UserServiceFindOne` }, UserService.findOne);
                                }}
                                to={`edit/${user.id}`}
                                type="button"
                            >
                                <PencilIcon className="size-5 scale-75 text-gray-800 transition-all hover:scale-100 hover:text-indigo-600" />
                            </NavLink>
                        </div>
                    ),
                    email: user.email || '-',
                    roles: user.roles.join(', '),
                    type: user.accountType,
                    username: (
                        <div
                            className={clsx('flex items-center gap-3', {
                                'text-red-500': user.locked,
                            })}
                        >
                            <span>{user.username}</span>
                            {user.locked && <LockClosedIcon className={'size-5'} data-testid={'user-locked'} />}
                        </div>
                    ),
                };
            }) || []
        );
    }, [activeTerritory, data, intl]);

    return (
        <div className="space-y-6 px-4">
            <div className="sm:flex sm:items-center">
                <div className="sm:flex-auto">
                    <h1 className="text-base font-semibold leading-6 text-gray-900">
                        <FormattedMessage id={'USERS_PAGE_HEADING'} />
                    </h1>

                    <p className="mt-2 text-sm text-gray-700">
                        <FormattedMessage id={'USERS_PAGE_DESCRIPTION'} />
                    </p>
                </div>
            </div>

            <div className={'flex items-center'}>
                <div className={'grow'}>
                    <SearchParametersSearchField placeholder={intl.formatMessage({ id: 'USERS_PAGE_SEARCH_PLACEHOLDER' })} />
                </div>

                <div className={'pl-4'}>
                    <SearchParametersOrderBy
                        options={[
                            { label: intl.formatMessage({ id: 'USERS_PAGE_ORDER_BY_NONE' }), value: '' },
                            { label: intl.formatMessage({ id: 'USERS_PAGE_ORDER_BY_USERNAME_DESC' }), value: 'username:desc' },
                            { label: intl.formatMessage({ id: 'USERS_PAGE_ORDER_BY_USERNAME_ASC' }), value: 'username:asc' },
                            { label: intl.formatMessage({ id: 'USERS_PAGE_ORDER_BY_ACCOUNT_TYPE_DESC' }), value: 'accountType:desc' },
                            { label: intl.formatMessage({ id: 'USERS_PAGE_ORDER_BY_ACCOUNT_TYPE_ASC' }), value: 'accountType:asc' },
                        ]}
                    />
                </div>
            </div>

            {error && (
                <p className="mt-2 text-sm text-gray-700">
                    <FormattedMessage id={'USERS_PAGE_ERROR_LOADING_DATA'} />
                </p>
            )}

            {isLoading && <Spinner />}

            {!error && !isLoading && (
                <div>
                    {rows.length > 0 ? (
                        <>
                            <div className={'overflow-auto'}>
                                <DataTable columns={columns} rows={rows} />
                            </div>

                            <div className={'border-t border-t-gray-200 pt-2'}>
                                <SearchParametersPagination total={rows.length} />
                            </div>
                        </>
                    ) : (
                        <p className="mt-2 text-sm text-gray-700">
                            <FormattedMessage id={'USERS_PAGE_NO_RESULTS'} />
                        </p>
                    )}
                </div>
            )}
        </div>
    );
};
